import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
} from '@mui/icons-material';

const Facturacion = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const columns = [
    { field: 'numero', headerName: 'Número', width: 150, renderCell: (params) => (
      <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
    )},
    { field: 'fecha', headerName: 'Fecha', width: 110 },
    { field: 'cliente', headerName: 'Cliente', width: 200 },
    { field: 'concepto', headerName: 'Concepto', width: 200, flex: 1 },
    { field: 'monto', headerName: 'Monto', width: 130, align: 'right', headerAlign: 'right', renderCell: (params) => (
      <Typography variant="body2" fontWeight={600}>${params.value.toLocaleString()}</Typography>
    )},
    { field: 'estado', headerName: 'Estado', width: 120, renderCell: (params) => (
      <Chip label={params.value} size="small" color={params.value === 'Cobrada' ? 'success' : params.value === 'Vencida' ? 'error' : 'warning'} />
    )},
    { field: 'acciones', headerName: 'Acciones', width: 150, sortable: false, renderCell: () => (
      <Box>
        <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
        <IconButton size="small"><PdfIcon fontSize="small" /></IconButton>
        <IconButton size="small"><SendIcon fontSize="small" /></IconButton>
      </Box>
    )},
  ];

  const rows = [
    { id: 1, numero: 'A-0001-00001234', fecha: '03/02/2026', cliente: 'YPF S.A.', concepto: 'Servicios Pozo XY-123', monto: 2500000, estado: 'Pendiente' },
    { id: 2, numero: 'A-0001-00001233', fecha: '01/02/2026', cliente: 'Pan American Energy', concepto: 'Workover MZ-789', monto: 1800000, estado: 'Pendiente' },
    { id: 3, numero: 'A-0001-00001232', fecha: '28/01/2026', cliente: 'Tecpetrol', concepto: 'Mantenimiento TK-001', monto: 720000, estado: 'Cobrada' },
    { id: 4, numero: 'A-0001-00001231', fecha: '20/01/2026', cliente: 'Vista Oil', concepto: 'Perforación NW-012', monto: 3200000, estado: 'Vencida' },
  ];

  const resumenStats = [
    { label: 'Emitidas (Feb)', value: '$4.3M', color: theme.palette.primary.main },
    { label: 'Pendientes', value: '$4.3M', color: theme.palette.warning.main },
    { label: 'Vencidas', value: '$3.2M', color: theme.palette.error.main },
    { label: 'Cobradas (2026)', value: '$12.8M', color: theme.palette.success.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Facturación
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de facturas emitidas
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nueva Factura
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color={stat.color}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todas" />
            <Tab label="Pendientes" />
            <Tab label="Vencidas" />
            <Tab label="Cobradas" />
          </Tabs>
          <Box sx={{ height: 450 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Dialog Nueva Factura */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Factura</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select label="Cliente">
                  <MenuItem value="1">YPF S.A.</MenuItem>
                  <MenuItem value="2">Pan American Energy</MenuItem>
                  <MenuItem value="3">Tecpetrol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo">
                  <MenuItem value="A">Factura A</MenuItem>
                  <MenuItem value="B">Factura B</MenuItem>
                  <MenuItem value="E">Factura E</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Concepto" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto Neto" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="IVA (21%)" type="number" disabled />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Centro de Costo</InputLabel>
                <Select label="Centro de Costo">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Emitir Factura</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Facturacion;
